import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { PRODUCT_NAME } from "@shared/brand";
import { ArrowLeft, ThumbsDown, ThumbsUp } from "lucide-react";

/**
 * F&F first-login explainer (s117 gate 1). Copy is verbatim from
 * `docs/living/ff-onboarding-copy.md` v3 §3.
 *
 * - mode="interstitial": the one-time first-login gate. "Get started" POSTs the
 *   seen flag (server-persisted) then lets App.tsx re-render past the gate. No
 *   acknowledgment checkbox, no consent record — informational only.
 * - mode="reference": the same content at /ff-welcome (banner "Learn more"),
 *   with a "Back to dashboard" link and no gate behaviour.
 */

function Sub({ children }: { children: React.ReactNode }) {
  return <h2 className="font-serif text-lg font-medium text-foreground mt-7 mb-2">{children}</h2>;
}

export function FFExplainer({ mode }: { mode: "interstitial" | "reference" }) {
  const [, navigate] = useLocation();

  const seenMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/auth/ff-explainer-seen");
    },
    onSuccess: () => {
      // Re-fetch the user so ffExplainerSeen flips true and the App.tsx gate opens.
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
  });

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-background flex justify-center px-4 py-10">
      <div className="w-full max-w-[640px] rounded-2xl border border-warm-stone bg-white shadow-sm px-6 py-8 sm:px-10 sm:py-10">
        <h1 className="font-serif text-[28px] sm:text-[32px] leading-tight text-foreground mb-6">
          Welcome to {PRODUCT_NAME}{" "}
          <span className="text-muted-foreground text-[20px] sm:text-[22px]">(Friends &amp; Family testing)</span>
        </h1>

        <p className="text-[15px] text-foreground/90 leading-relaxed">
          Thanks for helping test the product before it launches publicly. Here's what you need to know
          before you start. <em>(I've also emailed you a pack of fictitious test documents — a made-up
          family's will, statements, and records — you can upload to try the document-import flow without
          using anything real.)</em>
        </p>

        <Sub>This is a testing environment.</Sub>
        <p className="text-[15px] text-foreground/90 leading-relaxed">
          The product is close to launch but not finished. You'll find features that don't quite work yet,
          copy that reads awkwardly, and the occasional bug. That's why you're here — to help me find what I
          can't find alone.
        </p>

        <Sub>Please don't enter real personal data.</Sub>
        <p className="text-[15px] text-foreground/90 leading-relaxed">Use placeholder values for everything sensitive:</p>
        <ul className="mt-2 space-y-1.5 text-[15px] text-foreground/90 list-disc pl-5">
          <li>Instead of your real SIN, use 123-456-789 or anything made-up</li>
          <li>Instead of real passwords, use "testpassword1" or similar</li>
          <li>Instead of real bank account numbers, make them up</li>
          <li>Don't upload actual wills, bank statements, or government documents</li>
        </ul>
        <p className="mt-3 text-[15px] text-foreground/90 leading-relaxed">
          Use realistic names, dates, and addresses if it helps you test flow — those aren't the concern.
          It's specifically the sensitive credentials and account numbers we're asking you to fake.
        </p>
        <p className="mt-3 text-[15px] text-foreground/90 leading-relaxed">Two reasons we're strict about this:</p>
        <ol className="mt-2 space-y-2 text-[15px] text-foreground/90 list-decimal pl-5">
          <li>
            <strong>The database will be wiped before public launch.</strong> Anything you enter will be
            deleted. Don't invest real information into something that's going away.
          </li>
          <li>
            <strong>The encryption architecture isn't fully in place yet.</strong> At production launch, the
            product will store certain sensitive data (bank account numbers, credit card numbers) on servers
            encrypted at rest, and keep other highly-sensitive data (SIN, passwords, government IDs) on your
            own computer only. Neither of those protections is fully deployed during F&amp;F testing. Holding
            yourself to "placeholder-only" means you never have to worry about it.
          </li>
        </ol>

        <Sub>What you're testing toward.</Sub>
        <p className="text-[15px] text-foreground/90 leading-relaxed">At public launch, the product will:</p>
        <ul className="mt-2 space-y-1.5 text-[15px] text-foreground/90 list-disc pl-5">
          <li>Store your estate plan data (names, dates, institutions, contact info, account numbers) on our servers</li>
          <li>Give you a downloadable encrypted file for highly-sensitive values you keep on your own computer</li>
          <li>Pre-fill government forms and notification letters from your stored data, leaving only truly-sensitive fields blank for your executor to fill in from the file</li>
          <li>Never make you type the same information twice</li>
        </ul>
        <p className="mt-3 text-[15px] text-foreground/90 leading-relaxed">
          We're not there yet. Your job is to tell us what the product feels like on the way there.
        </p>

        <Sub>How to give feedback.</Sub>
        <p className="text-[15px] text-foreground/90 leading-relaxed">
          Three easy ways, whatever's handy: the <strong>Feedback</strong> button in the bottom-right of
          every page (it grabs a screenshot automatically), the <strong className="inline-flex items-center gap-0.5 align-[-3px]"><ThumbsUp className="h-4 w-4" aria-hidden="true" />/<ThumbsDown className="h-4 w-4" aria-hidden="true" /></strong> buttons on individual
          sections, or just <strong>reply to the welcome email</strong>. Bugs, confusions, things you liked,
          things that felt off — all useful.
        </p>

        <Sub>When to stop testing.</Sub>
        <p className="text-[15px] text-foreground/90 leading-relaxed">
          Whenever you want. Just tell me. This commitment is on your terms.
        </p>

        <div className="mt-9">
          {mode === "interstitial" ? (
            <Button
              className="w-full"
              size="lg"
              onClick={() => seenMutation.mutate()}
              disabled={seenMutation.isPending}
              data-testid="ff-explainer-get-started"
            >
              {seenMutation.isPending ? "One moment…" : "Get started"}
            </Button>
          ) : (
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="text-[15px] font-medium text-primary hover:underline"
              data-testid="ff-welcome-back"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to dashboard
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
